import React, { memo } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Typography, Stack, Avatar, Chip, Card, CardContent, Box, Grid } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import DonutChart from '../DonutChart';
import strings from '../../../Globalization/index';
import consts from '../../../consts';
import GroupMinPNG from '../../../styles/img/assets/Group minus.png';
import GroupPusPNG from '../../../styles/img/assets/Group plus.png';
import CyclePassPNG from '../../../styles/img/assets/cycle pass.png';
import CycleErrorsPNG from '../../../styles/img/assets/cycle error.png';

export const StatisticsPieChart = ({ data, total, label }) => {
	return (
		<Box sx={{ width: '100%', height: 200, position: 'relative' }}>
				<ResponsiveContainer width="100%" height="100%">
					<PieChart width={154} height={180}>
						<Pie
							data={data}
							cx="50%"
							cy="50%"
							innerRadius={35}
							outerRadius={45}
							fill="#8884d8"
							dataKey="value"
							startAngle={450}
							endAngle={90}
						>
							{data.map((entry, index) => (<Cell fill={entry.color} key={`cell-${index}`} />))}

						</Pie>
					</PieChart>
				</ResponsiveContainer>
				<Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
					<Typography variant="h5" component="div" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{total}</Typography>
					<Typography variant="body2" sx={{ textAlign: 'center' }}>{label}</Typography>
				</Box>
			</Box>
	);

}

const skipModality = [consts.EquipmentType.WASHER, consts.EquipmentType.LIQUIDCHEMICAL, consts.EquipmentType.ENDOWASHER];

const taskStatus = consts.TaskStatus;

const ChartCard = styled(Card)(({ theme }) => ({
	display: 'flex',
	width: '100%',
	'& .MuiCardContent-root': {
		display: 'flex',
		flexDirection: 'row',
		flex: 1,
		'& .MuiGrid-container': {
			display: 'flex',
			flexDirection: 'row',
			flex: 1
		}
	}
}));

const StatisticsComponent = memo(({ loadRelease, dashboardList ,extraParams}) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const equipmentTypeId = Number(searchParams.get('equipmentTypeId')) || Number(searchParams.get('EquipmentTypeId')) || 0;
	const isBiDisplay = !skipModality.includes(Number(equipmentTypeId));
	let cycleCounts = dashboardList?.CycleCounts || 0,
		totalCycleReviewed = dashboardList?.TotalCycleReviewed || 0,
		totalCycleUnreviewed = cycleCounts - totalCycleReviewed,
		reviewedPer = Math.round(cycleCounts === 0 ? 0 : (totalCycleReviewed * 100) / cycleCounts),
		bIPass = dashboardList?.BIPass || 0,
		bIFail = dashboardList?.BIFail || 0,
		totalBICount = bIPass + bIFail,
		biPositivePer = Math.round(totalBICount === 0 ? 0 : (bIPass * 100) / totalBICount),
		tasksStatusComplete = dashboardList?.TaskStatusComplete || 0,
		taskStatusDue = dashboardList?.TaskStatusDue || 0,
		taskStatusPastDue = dashboardList?.TaskStatusPastDue || 0,
		taskStatusClose = dashboardList?.TaskStatusClose || 0,
		cycleCountPass = dashboardList?.CycleCountPass || 0,
		cycleCountAborted = dashboardList?.CycleCountAborted || 0,
		cycleCountHalted = dashboardList?.CycleCountHalted || 0
	const handleClick = ({ filedName, value, path }) => {
		let params = { disableLoadPref: true, hideTypeFilter: true, taskStatusId: 0, };
		if (filedName === 'cycleStatusId') {
			params['IsReviewed'] = consts.DefaultValues.IntegerNull;
		}
		params[filedName] = consts.isNodeServer ? value : Number(value);
		if (filedName === 'BIResult') {
			params['IsSterilized'] = consts.SterilizeStatus.True;
		}
		if (filedName === 'IsReviewed') {
			params['cycleStatusId'] = consts.DefaultValues.IntegerNull;
		}

		if (consts.isNodeServer ? value : Number(value)) {
			params[filedName] = value
		}

		if (path === 'dashboardtasks') {
			navigate(`/${path}?disableLoadPref=${params.disableLoadPref}&hideTypeFilter=${params.hideTypeFilter}&equipmentTypeId=${dashboardList?.EquipmentTypeId || 0}&taskStatusId=${params.taskStatusId}`)
		}
		if (path === 'dashboardcycles') {
			navigate(`/${path}?disableLoadPref=${params.disableLoadPref}&hideTypeFilter=${params.hideTypeFilter}&equipmentTypeId=${dashboardList?.EquipmentTypeId}&IsReviewed=${params.IsReviewed}&cycleStatusId=${params.cycleStatusId}&locationId=${extraParams.locationId}`)
		}
		if (path === 'dashboardincubator') {
			navigate(`/${path}?disableLoadPref=${params.disableLoadPref}&hideTypeFilter=${params.hideTypeFilter}&equipmentTypeId=${dashboardList?.EquipmentTypeId}&BIResult=${params.BIResult}&IsSterilized=${params.IsSterilized}&locationId=${extraParams.locationId}`)
		}
	};

	return (
		<Grid container size={12} sx={{ marginBottom: theme.spacing() }} spacing={1} rowSpacing={3}>
			{loadRelease === consts.LoadReleaseType.Parametric ? (
				<Grid container size={{ xs: 12, lg: 4 }}>
					<ChartCard>
						<CardContent>
							<Grid container>
								<Grid size={12}>
									<Typography sx={{ fontWeight: 'bold' }}>{strings.CyclesReviewed}</Typography>
								</Grid>
								<Grid size={12}>
									<Grid container direction="row" justifyContent="space-evenly" alignItems="stretch">
										<Grid size={6} sx={{ display: 'flex', alignItems: 'center' }}>
											<StatisticsPieChart
												data={[
													{ color: '#51ba4d', value: reviewedPer },
													{ color: '#fe1b1b', value: cycleCounts === 0 ? 0 : 100 - reviewedPer }
												]
												}
												total={cycleCounts}
												label={strings.CycleList}
											/>
										</Grid>
										<Grid size={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly' }}>
											<Stack spacing={1}>
												<Chip
													avatar={
														<Avatar alt={strings.Unreviewed} src={CycleErrorsPNG} />
													}
													onClick={(event) => handleClick({ filedName: 'IsReviewed', value: 0, path: 'dashboardcycles', event })}
													title={strings.Unreviewed}
													label={`${totalCycleUnreviewed} ${strings.Unreviewed}`}
													sx={{ color: '#fe1b1b', width: '12em' }}
												/>
												<Chip
													avatar={
														<Avatar alt={strings.Reviewed} src={CyclePassPNG} />
													}
													onClick={(event) => handleClick({ filedName: 'IsReviewed', value: 1, path: 'dashboardcycles', event })}
													title={strings.Reviewed}
													label={`${totalCycleReviewed} ${strings.Reviewed}`}
													sx={{ color: '#51ba4d', width: '12em' }}
												/>
											</Stack>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</CardContent>
					</ChartCard>
				</Grid>
			) : (
					<Grid container size={{ xs: 12, lg: 4 }} >
					{isBiDisplay && (
						<ChartCard>
							<CardContent>
								<Grid container>
									<Grid size={12}>
										<Typography sx={{ fontWeight: 'bold' }}> {strings.BIResult} </Typography>
									</Grid>
									<Grid size={12}>
										<Grid container direction="row" justifyContent="space-evenly" alignItems="stretch">
											<Grid size={6} sx={{ display: 'flex', alignItems: 'center' }} >
												<DonutChart
													dashboardList={[
														{ color: '#fe1b1b', value: biPositivePer },
														{
															color: '#51ba4d',
															value: totalBICount === 0 ? 0 : 100 - biPositivePer
														}
													]}
													centerText={totalBICount} size={86} valueLabel="BI"
													sx={{ width: '12em', cursor: "pointer" }}
												/>
											</Grid>
											<Grid size={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly' }} >
												<Stack spacing={1}>
													<Chip
														avatar={
															<Avatar alt={strings.NegativeBI} src={GroupMinPNG} />
														}
														onClick={(event) => handleClick({ filedName: 'BIResult', value: consts.BIResult.Negative, path: 'dashboardincubator', event })}
														title={strings.NegativeBI}
														label={`${bIFail} ${strings.Negative}`}
														sx={{ color: '#51ba4d', width: '12em', fontWeight: 'bold' }}
													/>
													<Chip
														avatar={
															<Avatar alt={strings.PositiveBI} src={GroupPusPNG} />
														}
														onClick={(event) => handleClick({ filedName: 'BIResult', value: consts.BIResult.Positive, path: 'dashboardincubator', event }
														)
														}
														title={strings.Reviewed}
														label={`${bIPass} ${strings.Positive}`}
														sx={{ color: '#fe1b1b', width: '12em', fontWeight: 'bold' }}
													/>
												</Stack>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</CardContent>
						</ChartCard>
					)}
				</Grid>
			)}
			<Grid container size={{ xs: 12, lg: 4 }}>
				<ChartCard>
					<CardContent>
						<Grid container>
							<Grid size={12}>
								<Typography sx={{ fontWeight: 'bold' }}> {strings.Tasks} </Typography>
							</Grid>
							<Grid size={12}>
								<Grid container direction="row" justifyContent="space-evenly" alignItems="stretch" >
									<Grid size={6} sx={{ display: 'flex', alignItems: 'center' }}>
										<StatisticsPieChart
											data={[
												{
													color: '#ff0',
													value: taskStatusDue
												},
												{
													color: '#fe1b1b',
													value: taskStatusPastDue
												},
												{
													color: '#92d050',
													value: tasksStatusComplete
												},
												{
													color: '#00b050',
													value: taskStatusClose
												}
											]}
											total={dashboardList.TaskCount || 0}
											label={strings.Tasks}
										/>
									</Grid>
									<Grid size={6} sx={{ display: "flex", justifyContent: 'space-evenly' }}>
										<Stack spacing={1} sx={{ paddingTop: theme.spacing(0.5) }}>
											<Chip
												onClick={(event) => handleClick({ filedName: 'taskStatusId', value: taskStatus.Due, path: 'dashboardtasks', event })}
												title={strings.DueTasks}
												label={`${taskStatusDue} ${strings.Due}`}
												color="default"
												sx={{ width: '11.5em', cursor: 'pointer', fontWeight: 'bold', color: '#000000', backgroundColor: '#ff0', display: 'flex', justifyContent: 'flex-start', '&:hover': { backgroundColor: '#ff0' } }}
											/>
											<Chip
												onClick={(event) => handleClick({ filedName: 'taskStatusId', value: taskStatus.PastDue, path: 'dashboardtasks', event })}
												title={strings.PastDueTasks}
												label={`${taskStatusPastDue} ${strings.PastDue}`}
												color="default"
												sx={{ width: '11.5em', cursor: 'pointer', fontWeight: 'bold', color: '#000000', backgroundColor: '#ff0000', display: 'flex', justifyContent: 'flex-start', '&:hover': { backgroundColor: '#ff0000' } }}
											/>
											<Chip
												onClick={(event) => handleClick({ filedName: 'taskStatusId', value: taskStatus.Complete, path: 'dashboardtasks', event })}
												title={strings.CompleteTasks}
												label={`${tasksStatusComplete} ${strings.Complete}`}
												color="default"
												sx={{ width: '11.5em', cursor: 'pointer', fontWeight: 'bold', color: '#000000', backgroundColor: '#92d050', display: 'flex', justifyContent: 'flex-start', '&:hover': { backgroundColor: '#92d050' } }}
											/>

											<Chip
												onClick={(event) => handleClick({ filedName: 'taskStatusId', value: taskStatus.ClosedNoAction, path: 'dashboardtasks', event })}
												title={strings.ClosedNoAction}
												label={`${taskStatusClose} ${strings.ClosedNoAction}`}
												color="default"
												sx={{ width: '11.5em', cursor: 'pointer', fontWeight: 'bold', color: '#000000', backgroundColor: "#00b050", display: 'flex', justifyContent: 'flex-start', '&:hover': { backgroundColor: '#00b050' } }}
											/>
										</Stack>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</CardContent>
				</ChartCard>
			</Grid>
			<Grid container size={{ xs: 12, lg: 4 }}>
				<ChartCard>
					<CardContent>
						<Grid container>
							<Grid size={12}>
								<Typography sx={{ fontWeight: 'bold' }}> {strings.CyclesPerformed} </Typography>
							</Grid>
							<Grid size={12}>
								<Grid container direction="row" justifyContent="space-evenly" alignItems="stretch" >
									<Grid size={6} sx={{ display: 'flex', alignItems: 'center' }}>
										<StatisticsPieChart
											data={[
												{
													color: '#51ba4d',
													value: cycleCountPass
												},
												{
													color: '#fe1b1b',
													value: cycleCountAborted + cycleCountHalted
												}
											]}
											total={cycleCounts}
											label={strings.CycleList}
										/>
									</Grid>
									<Grid size={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly' }}>
										<Stack spacing={1}>
											<Chip
												onClick={(event) => handleClick({ filedName: 'cycleStatusId', value: consts.CycleStatus.Pass, path: 'dashboardcycles', event })}
												avatar={<Avatar alt={strings.Pass} src={CyclePassPNG} />}
												title={strings.DueTasks}
												label={`${cycleCountPass} ${strings.Pass}`}
												sx={{ width: '12em', color: '#51ba4d' }}
											/>
											<Chip
												onClick={(event) => handleClick({ filedName: 'cycleStatusId', value: consts.CycleStatus.Aborted, path: 'dashboardcycles', event })}
												avatar={<Avatar alt={strings.Aborted} src={CycleErrorsPNG} />}
												title={strings.DueTasks}
												label={`${cycleCountAborted + cycleCountHalted} ${strings.Aborted}`}
												sx={{ width: '12em', color: '#fe1b1b' }}
											/>
										</Stack>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</CardContent>
				</ChartCard>
			</Grid>
		</Grid>
	)
})

export default StatisticsComponent